import { useEffect, useState, type ReactNode, type FormEvent } from 'react';
import { apiRequest } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { useTranslation } from '../../../../shared/i18n/index.js';

interface FrameworkRow {
  readonly frameworkId: string;
  readonly frameworkName: string;
}

interface ControlRow {
  readonly controlId: string;
  readonly controlCode: string;
  readonly controlName: string;
}

/**
 * GR L30.07 — Compliance Evidence (`/security/grc/evidence`). Evidence-capture
 * surface for a `compliance_control`: pick a framework, pick one of its
 * controls, then attach an evidence reference (posted to the control's evidence
 * ledger). Enforces that a control is chosen and a reference supplied.
 */
export function GrEvidencePage(): ReactNode {
  const { t } = useTranslation();
  const { token } = useAuth();
  const [frameworks, setFrameworks] = useState<FrameworkRow[]>([]);
  const [frameworkId, setFrameworkId] = useState('');
  const [controls, setControls] = useState<ControlRow[]>([]);
  const [controlId, setControlId] = useState('');
  const [evidenceRef, setEvidenceRef] = useState('');
  const [validation, setValidation] = useState<string | null>(null);
  const [submitted, setSubmitted] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    let alive = true;
    apiRequest<{ data: FrameworkRow[] }>('/v1/compliance/frameworks', { token })
      .then((r) => {
        if (!alive) return;
        setFrameworks(r.data);
        setError(null);
        if (r.data.length > 0) setFrameworkId((cur) => cur || r.data[0].frameworkId);
        else setLoading(false);
      })
      .catch((e) => { if (alive) { setError((e as Error).message); setLoading(false); } });
    return () => { alive = false; };
  }, [token]);

  useEffect(() => {
    if (!frameworkId) return;
    let alive = true;
    setLoading(true);
    apiRequest<{ data: ControlRow[] }>(`/v1/compliance/frameworks/${frameworkId}/controls`, { token })
      .then((r) => {
        if (!alive) return;
        setControls(r.data);
        setControlId(r.data.length > 0 ? r.data[0].controlId : '');
        setError(null);
      })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, frameworkId]);

  async function submit(e: FormEvent): Promise<void> {
    e.preventDefault();
    if (!controlId) { setValidation(t('gr30.evidence.valNoControl')); return; }
    if (evidenceRef.trim().length < 3) { setValidation(t('gr30.evidence.valRef')); return; }
    setValidation(null);
    setSubmitting(true);
    try {
      await apiRequest(`/v1/compliance/controls/${controlId}/evidence`, {
        token, method: 'POST', body: { evidenceRef: evidenceRef.trim() },
      });
      setSubmitted(t('gr30.evidence.submitted'));
      setEvidenceRef('');
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main aria-label="security-evidence">
      <h1>{t('gr30.evidence.title')}</h1>
      {error && <p role="alert">{error}</p>}
      {submitted && <p aria-label="evidence-submitted" role="status">{submitted}</p>}
      {validation && <p aria-label="validation" role="status">{validation}</p>}
      {loading ? <p>{t('gr30.evidence.loading')}</p> : frameworks.length === 0 ? (
        <p aria-label="empty">{t('gr30.evidence.emptyFrameworks')}</p>
      ) : (
        <form onSubmit={submit}>
          <label>
            {t('gr30.evidence.labelFramework')}
            <select aria-label="filter-framework" value={frameworkId} onChange={(e) => setFrameworkId(e.target.value)}>
              {frameworks.map((f) => <option key={f.frameworkId} value={f.frameworkId}>{f.frameworkName}</option>)}
            </select>
          </label>
          <label>
            {t('gr30.evidence.labelControl')}
            <select aria-label="select-control" value={controlId} onChange={(e) => setControlId(e.target.value)}>
              {controls.length === 0 && <option value="">{t('gr30.evidence.noControls')}</option>}
              {controls.map((c) => <option key={c.controlId} value={c.controlId}>{c.controlCode} — {c.controlName}</option>)}
            </select>
          </label>
          <label>
            {t('gr30.evidence.labelEvidenceRef')}
            <input aria-label="input-evidence-ref" value={evidenceRef} onChange={(e) => setEvidenceRef(e.target.value)} />
          </label>
          <button type="submit" aria-label="submit-evidence" disabled={submitting}>
            {submitting ? t('gr30.evidence.submitting') : t('gr30.evidence.submit')}
          </button>
        </form>
      )}
    </main>
  );
}
